import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { LangProvider, useLang } from "../lib/lang-context";
import { ThemeProvider } from "../lib/theme-context";
import { Toaster } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { ArrowLeft, ArrowUpRight, Clock } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Index = () => {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`, { params: { lang } })
      .then((res) => setPosts(res.data.posts || []))
      .catch(() => setPosts([]));
  }, [lang]);

  return (
    <main className="pt-44 pb-24" data-testid="blog-index">
      <SEO
        title="Blog ICOR · Insights en distribución médica"
        description="Artículos sobre logística médica, cadena fría, cumplimiento COFEPRIS, EPP hospitalario y mejores prácticas de aprovisionamiento para hospitales y clínicas."
        canonical="https://icor.mx/blog"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link
          to="/"
          data-testid="blog-back"
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-zinc-400 hover:text-[var(--icor-navy)] dark:hover:text-white mb-8"
        >
          <ArrowLeft size={14} /> {t.blog.backHome}
        </Link>
        <Reveal>
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            {t.blog.eyebrow}
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter mt-5 text-[var(--icor-navy)] dark:text-white text-balance max-w-3xl">
            {t.blog.title}
          </h1>
          <p className="mt-5 text-lg text-slate-600 dark:text-zinc-400 max-w-2xl">
            {t.blog.subtitle}
          </p>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 80} className="h-full">
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-index-card-${p.slug}`}
                className="card-lift group bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 overflow-hidden flex flex-col h-full"
                style={{ borderRadius: 4 }}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-zinc-800">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-black/70 backdrop-blur-sm px-2.5 py-1">
                    {p.tag}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl font-medium tracking-tight text-[var(--icor-navy)] dark:text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 pt-5 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between text-xs uppercase tracking-[0.18em] font-semibold">
                    <span className="text-slate-500 dark:text-zinc-500">{p.date}</span>
                    <span className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-500">
                      <Clock size={12} weight="bold" /> {p.read}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
};

const Article = ({ slug }) => {
  const { t, lang } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios
      .get(`${API}/blog/posts/${slug}`, { params: { lang } })
      .then((res) => setPost(res.data))
      .catch(() => setError(true));
  }, [slug, lang]);

  if (error)
    return (
      <main className="pt-44 pb-24 max-w-3xl mx-auto px-6 lg:px-8" data-testid="blog-article-not-found">
        <h1 className="font-display text-3xl text-[var(--icor-navy)] dark:text-white">
          Not found
        </h1>
        <Link to="/blog" className="text-[var(--icor-blue)] mt-4 inline-block">
          ← {t.blog.viewAll}
        </Link>
      </main>
    );

  if (!post)
    return (
      <main className="pt-44 pb-24 max-w-3xl mx-auto px-6 lg:px-8" data-testid="blog-article-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-3 w-24 bg-slate-200 dark:bg-zinc-800" />
          <div className="h-10 w-3/4 bg-slate-200 dark:bg-zinc-800" />
          <div className="h-4 w-1/3 bg-slate-200 dark:bg-zinc-800" />
        </div>
      </main>
    );

  return (
    <main className="pt-44 pb-24" data-testid={`blog-article-${slug}`}>
      <SEO
        title={`${post.title} · Blog ICOR`}
        description={post.excerpt || post.title}
        canonical={`https://icor.mx/blog/${slug}`}
        image={post.image}
        type="article"
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <Link
          to="/blog"
          data-testid="blog-article-back"
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-zinc-400 hover:text-[var(--icor-navy)] dark:hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> {t.blog.viewAll}
        </Link>
        <Reveal>
          <div className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
            {post.tag}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter mt-4 text-[var(--icor-navy)] dark:text-white text-balance">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-zinc-400">
            <span>
              {t.blog.published} · {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={12} weight="bold" /> {post.read}
            </span>
          </div>
        </Reveal>
      </div>
      <div className="mt-10 max-w-5xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div
            className="overflow-hidden border border-slate-200 dark:border-zinc-800"
            style={{ borderRadius: 4 }}
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        </Reveal>
      </div>
      <article className="mt-12 max-w-3xl mx-auto px-6 lg:px-8">
        {(post.body || []).map((para, i) => (
          <Reveal key={i} delay={i * 50}>
            <p className="text-lg text-slate-700 dark:text-zinc-300 leading-relaxed mb-6">
              {para}
            </p>
          </Reveal>
        ))}
        <div
          className="mt-12 p-6 bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800"
          style={{ borderRadius: 4 }}
        >
          <div className="text-xs tracking-[0.18em] uppercase font-bold text-[var(--icor-blue)] mb-2">
            ICOR
          </div>
          <p className="text-base text-slate-700 dark:text-zinc-300">
            {post.excerpt}
          </p>
          <Link
            to="/#quote"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[var(--icor-blue)] hover:underline"
          >
            {t.nav.cta}
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </article>
    </main>
  );
};

const BlogContent = ({ slug }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      <Header />
      {slug ? <Article slug={slug} /> : <Index />}
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default function BlogPage() {
  const { slug } = useParams();
  return (
    <ThemeProvider>
      <LangProvider>
        <BlogContent slug={slug} />
      </LangProvider>
    </ThemeProvider>
  );
}
